import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import MHeader from "@/components/ui/organisms/MHeader";
import { getMemberInfo } from "@/hooks/useMembers";
import { DropdownOption } from "@/components/ui/atoms/MOptionsMenu";
import ReminderCard from "./ReminderCard";
import { useReminders } from "@/hooks/useReminders";
import {
  BaseReminders,
  Reminders,
  ReminderSubType,
  ReminderType,
} from "@/types/reminders";
import {
  IconSearch,
  IconEdit,
  IconCaretDownFilled,
  IconInfoSquare,
  IconCircleX,
  IconPillFilled,
  IconBellX,
  IconCalendar,
  IconShieldHeart,
  IconVaccine,
  IconPaw,
  IconRefresh,
  IconBell,
  IconDna,
  IconUsersGroup,
} from "@tabler/icons-react";
import styles from "./reminder.module.css";
import MDialog from "@/components/ui/atoms/MDialog";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import MLoader from "@/components/ui/atoms/MLoader";
import RemindersListingSkeleton from "./RemindersListingSkeleton";
import { useNavigate } from "react-router-dom";
import { vaccinationsService } from "@/services/vaccinations.service";
import usePetCare from "@/hooks/usePetCare";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setSelectedMember, clearSelectedMember } from "@/store/authSlice";
import { useManagedMembersContext } from "@/context/ManagedMembersContext";
import { updateNavigationSourceMemberId } from "@/utils/navigationSource";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import { getEmphasizedText, getTertiaryText } from "./remindersHelper";
import ReminderViewModal from "./ReminderViewModal";
import { MTourTrigger } from "@/components/ui/organisms/MTourTrigger";
import { MHelpIcon } from "@/components/ui/atoms/MHelpIcon";
import { useNavigationSource } from "@/hooks/useNavigationSource";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

// Sentinel value for the member-filter drawer's "All" option — distinct
// from any real member id.
const ALL_MEMBERS_FILTER_VALUE = "ALL_MEMBERS";

const getMemberInitials = (firstName?: string, lastName?: string): string =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();

/**
 * RemindersListing component
 * Displays a list of reminders with search, filter, and CRUD operations
 */
interface RemindersListingProps {
  onAddReminder: () => void;
}

const RemindersListing: React.FC<RemindersListingProps> = ({
  onAddReminder,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tourTriggerRef = React.useRef<any>(null);
  const memberInfo = getMemberInfo();
  const { navigateBack, hasNavigationSource } = useNavigationSource();
  const memberId = memberInfo.id || "";
  // The actual logged-in member, ignoring whatever `selectedMember` may
  // already be set to — needed to tell "my own reminder" apart from a
  // family member's reminder regardless of how this screen was entered.
  const loggedInMemberId = getMemberInfo(true).id || "";
  const { managedMembers, fetchManagedMembers } = useManagedMembersContext();
  // Get member name as requested for personalized messages
  const {
    reminders,
    loading,
    fetchMyReminders: fetchReminders,
    fetchMoreMyReminders: fetchMoreReminders,
    deleteReminder,
    memberFilter,
    setMemberFilter,
    searchTerm,
    setSearchTerm,
    hasMoreMine: hasMore,
    isLoadingMoreMine: isLoadingMore,
  } = useReminders(memberId);
  const [inputValue, setInputValue] = useState(searchTerm);
  // null = "All" (no member filter)
  const [selectedMemberId, setSelectedMemberIdState] = useState<string | null>(
    null
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedReminderId, setSelectedReminderId] = useState<string | null>(
    null
  );
  const [selectedReminderMemberId, setSelectedReminderMemberId] = useState<
    string | undefined
  >(undefined);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [viewingReminder, setViewingReminder] = useState<Reminders | null>(
    null
  );
  const [showMemberFilterDrawer, setShowMemberFilterDrawer] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // Gates the skeleton to the very first fetch only — never reset back to
  // false, so later filter/member-driven refetches use the lightweight
  // MLoader instead of re-showing the full skeleton (matches
  // DocumentsLanding's hasLoadedOnce convention).
  const hasLoadedOnce = React.useRef(false);
  const { getPetCare } = usePetCare(memberId);
  const [petCareDataMap, setPetCareDataMap] = useState<
    Record<
      string,
      { frequencyType: string; frequency?: number; nextDueDate?: Date }
    >
  >({});

  // Needed up-front (not just when editing a reminder) so the member-filter
  // drawer has more than just "All" to show as soon as it's opened.
  useEffect(() => {
    fetchManagedMembers();
  }, []);

  // Fetch reminders when component mounts, member changes, or the member
  // filter changes
  useEffect(() => {
    if (memberId) {
      (async () => {
        setInitialLoadDone(false);
        await fetchReminders();
        setInitialLoadDone(true);
        hasLoadedOnce.current = true;
      })();
    }
  }, [memberId, memberFilter, fetchReminders]);

  useEffect(() => {
    const fetchPetCareData = async () => {
      const petCareReminders = reminders.filter(
        (r) => r.type === ReminderType.PET_CARE
      );
      const newPetCareDataMap: Record<
        string,
        { frequencyType: string; frequency?: number }
      > = {};

      for (const reminder of petCareReminders) {
        if (reminder.sourceId && !petCareDataMap[reminder.sourceId]) {
          try {
            const petCare = await getPetCare(reminder.sourceId);
            if (petCare) {
              newPetCareDataMap[reminder.sourceId] = {
                frequencyType: petCare.frequencyType,
                frequency: petCare.frequency,
                nextDueDate: petCare.nextDueDate,
              };
            }
          } catch (error) {
            console.error("Error fetching pet care data:", error);
          }
        }
      }

      if (Object.keys(newPetCareDataMap).length > 0) {
        setPetCareDataMap((prev) => ({ ...prev, ...newPetCareDataMap }));
      }
    };

    if (reminders.length > 0) {
      fetchPetCareData();
    }
  }, [reminders, getPetCare]);

  // Handle search when user clicks search icon
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Handle member-filter selection ("ALL_MEMBERS" sentinel clears the filter
  // back to showing every family member's reminders)
  const handleMemberFilterSelect = (value: string) => {
    setShowMemberFilterDrawer(false);
    const nextMemberId = value === ALL_MEMBERS_FILTER_VALUE ? null : value;
    setSelectedMemberIdState(nextMemberId);
    setMemberFilter(nextMemberId);
    // No need to call fetchReminders() here as the useEffect will handle it
  };

  // Generate actions for each reminder item
  const getReminderActions = (reminder: BaseReminders): DropdownOption[] => {
    return [
      {
        id: "edit",
        label: t("common.edit"),
        icon: <IconEdit size={20} />,
        onClick: () => handleEditReminder(reminder),
        isVisible: true,
      },
      {
        id: "delete",
        label: t("common.delete"),
        icon: <IconCircleX size={20} className="text-semantic-icon-error" />,
        onClick: () => showDeleteReminderDialog(reminder.id, reminder.memberId),
        isVisible: true,
        className: "text-semantic-icon-error",
      },
    ];
  };

  // "All" + every managed family member (Figma 5257-59660) — filters this
  // listing down to reminders set FOR that specific member.
  const getMemberFilterItems = (): {
    value: string;
    label: string;
    icon: React.ReactNode;
  }[] => {
    const sortedMembers = [...managedMembers].sort((a, b) => {
      if (a.id === loggedInMemberId) return -1;
      if (b.id === loggedInMemberId) return 1;
      return (a.first_name || "").localeCompare(b.first_name || "");
    });
    return [
      {
        value: ALL_MEMBERS_FILTER_VALUE,
        label: t("reminders.allMembers"),
        icon: (
          <div className={styles.memberDrawerAvatarOutline}>
            <IconUsersGroup size={20} />
          </div>
        ),
      },
      ...sortedMembers.map((member) => {
        const isYou = member.id === loggedInMemberId;
        const registered = isYou || member.registration_status === "accepted";
        return {
          value: member.id,
          label: isYou
            ? t("reminders.addReminder.you")
            : member.nickname || member.first_name || "",
          icon: member.avatar ? (
            <div
              className={
                registered
                  ? styles.memberDrawerAvatar
                  : styles.memberDrawerAvatarOutline
              }
            >
              <img
                src={member.avatar}
                alt=""
                className={styles.memberDrawerAvatarImg}
              />
            </div>
          ) : (
            <div
              className={
                registered
                  ? styles.memberDrawerAvatar
                  : styles.memberDrawerAvatarOutline
              }
            >
              {getMemberInitials(member.first_name, member.last_name)}
            </div>
          ),
        };
      }),
    ];
  };

  const getIcon = (reminder: BaseReminders) => {
    switch (reminder.type) {
      case ReminderType.MEDICATION:
        return <IconPillFilled size={24} />;
      case ReminderType.APPOINTMENT:
        return <IconCalendar size={24} />;
      case ReminderType.INSURANCE:
        return <IconShieldHeart size={24} />;
      case ReminderType.VACCINATION:
        return <IconVaccine size={24} />;
      case ReminderType.PET_CARE:
        return <IconPaw size={24} />;
      case ReminderType.VITALS:
        return <IconDna size={24} />;
      default:
        return <IconInfoSquare size={24} />;
    }
  };

  // The medication/appointment/insurance/pet-care edit pages all resolve
  // their working member via getMemberInfo(), which reads Redux
  // auth.selectedMember — the same mechanism the family profile picker
  // uses elsewhere in the app. Since this listing now spans reminders for
  // multiple family members, editing one created for someone other than
  // the logged-in member must switch that selection first, so the edit
  // page picks up the right member on its own next render.
  // Returns false when the target member couldn't be resolved (stale
  // managedMembers cache, revoked access, a fetch race) — callers must
  // check this and abort rather than proceeding with whatever member
  // context happened to already be active, which would operate on this
  // reminder under the WRONG acting member.
  const switchToReminderMember = async (
    reminder: BaseReminders
  ): Promise<boolean> => {
    const targetMemberId = reminder.memberId;
    if (!targetMemberId || targetMemberId === loggedInMemberId) {
      dispatch(clearSelectedMember());
      return true;
    }
    const members =
      managedMembers.length > 0 ? managedMembers : await fetchManagedMembers();
    const targetMember = members.find((m) => m.id === targetMemberId);
    if (!targetMember) {
      console.error(
        `Could not find managed member ${targetMemberId} for reminder ${reminder.id}`
      );
      return false;
    }
    dispatch(setSelectedMember(targetMember));
    updateNavigationSourceMemberId(targetMemberId);
    return true;
  };

  // Handle edit reminder
  const handleEditReminder = async (reminder: BaseReminders) => {
    // Navigate to edit page or open edit modal
    setSelectedReminderId(reminder.id);
    const switched = await switchToReminderMember(reminder);
    if (!switched) {
      showToast({
        message: t("reminders.errors.errorSwitchingMember"),
        variant: "error",
      });
      return;
    }
    switch (reminder.type) {
      case ReminderType.MEDICATION:
        navigate(`/reminders/medication`, { state: { reminder } });
        break;
      case ReminderType.APPOINTMENT:
        navigate(`/reminders/appointment`, { state: { reminder } });
        break;
      case ReminderType.INSURANCE:
        navigate(`/reminders/insurance`, { state: { reminder } });
        break;
      case ReminderType.VACCINATION:
        try {
          // Fetch with the reminder's own target member directly — the
          // redux selectedMember switch above only takes effect once the
          // *next* page renders, so it can't be relied on for this
          // synchronous fetch.
          const vaccination = await vaccinationsService.getVaccination(
            reminder.memberId || memberId,
            reminder.sourceId
          );
          navigate(`/reminders/add-vaccine`, { state: { vaccination } });
        } catch (error) {
          showToast({
            message: t("reminders.errors.errorFetchingVaccination"),
            variant: "error",
          });
        }
        break;
      case ReminderType.PET_CARE:
        try {
          // Fetch with the reminder's own target member directly — same
          // reasoning as the vaccination case above, the redux
          // selectedMember switch only takes effect on the *next* render.
          const petCare = await getPetCare(reminder.sourceId);
          navigate("/reminders/pet-care/add-update-schedule", {
            state: { petCareType: petCare.type, petCare },
          });
        } catch (error) {
          showToast({
            message: t("reminders.errors.errorFetchingPetCare"),
            variant: "error",
          });
        }
        break;
      case ReminderType.VITALS:
        navigate("/reminders/vitals", { state: { reminder } });
        break;
      default:
        break;
    }
  };

  const handleEditReminderDone = () => {
    setShowEditDrawer(false);
    setSelectedReminderId(null);
    fetchReminders();
  };

  const showDeleteReminderDialog = (
    reminderId: string,
    reminderMemberId?: string
  ) => {
    setSelectedReminderId(reminderId);
    setSelectedReminderMemberId(reminderMemberId);
    setShowDeleteDialog(true);

    // Track delete click
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_HOME,
      action: "delete_click",
    });
  };

  // Handle delete reminder
  const handleDeleteReminder = async () => {
    // show delete confirmation dialog — pass the reminder's own target
    // member (not the logged-in member) since the listing now spans
    // reminders created for other family members too
    await deleteReminder(selectedReminderId, selectedReminderMemberId);
    setSelectedReminderId(null);
    setSelectedReminderMemberId(undefined);
    setShowDeleteDialog(false);

    // Track delete confirmation
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_HOME,
      action: "delete_confirm",
    });

    fetchReminders();
  };

  return (
    <>
      {!hasNavigationSource() && (
        <div className={styles.headerHelpIcon} data-wizard-help-icon>
          <MHelpIcon onClick={() => tourTriggerRef.current?.open()} />
        </div>
      )}
      {/* Header */}
      <MHeader
        module="reminders_listing"
        memberInfo={memberInfo}
        headerLabel="Reminders"
        titleSize="lg"
        className={styles.headerCurve}
        onAdd={onAddReminder}
        // showSearch={selectedCategory !== ReminderType.ALL}
        searchPlaceholder={t("reminders.listing.searchPlaceholder")}
        value={inputValue}
        onValueChange={setInputValue}
        onSearch={handleSearch}
        onClear={() => {
          setInputValue("");
          setSearchTerm("");
        }}
        showBackButton={hasNavigationSource()}
        onBack={navigateBack}
        description={
          "Set reminders for anyone in the family. The notifications come only to you."
        }
        learnMoreContent={
          <>
            When you set a reminder—no matter who it is for—it will appear only
            in your profile, and the alert will be sent only to your phone.
            <br /> <br /> For example, if you set a reminder for your father's
            blood test, the notification will come to your device at the date
            and time you've set.
          </>
        }
      />

      {loading ? (
        hasLoadedOnce.current ? (
          <MLoader />
        ) : (
          <RemindersListingSkeleton />
        )
      ) : (
        <>
          {initialLoadDone &&
            reminders.length === 0 &&
            !searchTerm &&
            selectedMemberId === null && (
              <>
                <MSpacer mt={24} />
                <MEmptyCard
                  primaryTitle={t("reminders.listing.noRemindersSet")}
                  secondaryTitle={t("reminders.listing.noRemindersHelp")}
                  icon={<IconBellX />}
                  primaryCTA={{
                    text: t("reminders.listing.addReminder"),
                    onClick: onAddReminder,
                  }}
                />
              </>
            )}

          {/* Reminders list */}
          <div>
            <MSpacer mt={24} />
            <div className={styles.listingContainer}>
              {(reminders.length > 0 || selectedMemberId !== null) && (
                <button
                  type="button"
                  className={styles.filterDropdown}
                  onClick={() => setShowMemberFilterDrawer(true)}
                  aria-label={t("reminders.memberFilterTitle")}
                >
                  <span>
                    {selectedMemberId === null
                      ? t("reminders.allMembers")
                      : selectedMemberId === loggedInMemberId
                      ? t("reminders.addReminder.you")
                      : managedMembers.find((m) => m.id === selectedMemberId)
                          ?.nickname ||
                        managedMembers.find((m) => m.id === selectedMemberId)
                          ?.first_name ||
                        t("reminders.allMembers")}
                  </span>
                  <IconCaretDownFilled size={16} />
                </button>
              )}

              {initialLoadDone && reminders.length === 0 && searchTerm && (
                <div className="w-full">
                  <MEmptyCard
                    primaryTitle={t("reminders.searchEmpty")}
                    secondaryTitle={t("reminders.searchEmptyDescription")}
                    icon={<IconSearch />}
                  />
                </div>
              )}

              {initialLoadDone &&
                reminders.length === 0 &&
                !searchTerm &&
                selectedMemberId !== null && (
                  <div className="w-full">
                    <MEmptyCard
                      primaryTitle={t("reminders.categoryEmpty")}
                      icon={<IconBellX />}
                    />
                  </div>
                )}

              {reminders.length > 0 && (
                <MInfiniteScroll
                  data={reminders}
                  hasMore={hasMore}
                  isLoading={isLoadingMore}
                  onLoadMore={fetchMoreReminders}
                  className={styles.cardsList}
                  gap={0}
                  renderItem={(reminder) => {
                    const isTherapy =
                      reminder.type === ReminderType.APPOINTMENT &&
                      reminder.subType === ReminderSubType.THERAPY;
                    const petCareData =
                      reminder.type === ReminderType.PET_CARE &&
                      reminder.sourceId
                        ? petCareDataMap[reminder.sourceId]
                        : undefined;
                    const hasFrequency =
                      reminder.type !== ReminderType.APPOINTMENT || isTherapy;

                    return (
                      <ReminderCard
                        key={reminder.id}
                        icon={getIcon(reminder)}
                        badgeLabel={
                          reminder.onBehalf && reminder.name
                            ? t("reminders.listing.familyBadge", {
                                name: reminder.name,
                              })
                            : t("reminders.listing.yours")
                        }
                        isOwn={!reminder.onBehalf}
                        title={reminder.reminderFor}
                        onTitleClick={() => setViewingReminder(reminder)}
                        secondaryText={getEmphasizedText(
                          reminder,
                          t,
                          petCareData
                        )}
                        secondaryIcon={
                          hasFrequency ? (
                            <IconRefresh size={16} color="#2A2A2A" />
                          ) : undefined
                        }
                        tertiaryText={getTertiaryText(
                          reminder,
                          petCareData?.nextDueDate
                        )}
                        tertiaryIcon={<IconBell size={16} />}
                        actions={getReminderActions(reminder)}
                      />
                    );
                  }}
                />
              )}
            </div>
          </div>

          {/* Delete reminder modal */}
          <MDialog
            isOpen={showDeleteDialog}
            onClose={() => setShowDeleteDialog(false)}
            title={t("reminders.deleteReminder.title")}
            titleFlush
            description={t("reminders.deleteReminder.description")}
            primaryCtaText={t("common.yes")}
            onPrimaryCta={handleDeleteReminder}
            secondaryCtaText={t("common.no")}
            onSecondaryCta={() => setShowDeleteDialog(false)}
            showCloseButton={true}
          />

          {/* Member filter drawer — capped height (not the default "auto",
              which grows to fit every member with no limit) so a family
              with many members scrolls inside the drawer instead of it
              opening almost full-screen. */}
          <MDropDownDrawer
            open={showMemberFilterDrawer}
            onClose={() => setShowMemberFilterDrawer(false)}
            title={t("reminders.memberFilterTitle")}
            initialValue={selectedMemberId ?? ALL_MEMBERS_FILTER_VALUE}
            items={getMemberFilterItems()}
            onSelect={(value) => handleMemberFilterSelect(value as string)}
            variant="single"
            height="55vh"
          />

          <ReminderViewModal
            reminder={viewingReminder}
            isOpen={!!viewingReminder}
            onClose={() => setViewingReminder(null)}
          />
        </>
      )}

      <MTourTrigger ref={tourTriggerRef} page="reminders" />
    </>
  );
};

export default RemindersListing;
